import Image, { ImageProps } from 'next/image';

import { cn } from '@/lib/utils';

import { HTMLDataTrackerProps } from '@/app/types';
import { debucketizeImageUrl } from '@/decodo/components/atoms/Image/utils';

export type NextImageProps = {
  classNames?: {
    container?: string;
    image?: string;
  };
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
  placeholder?: 'blur' | 'empty';
  customSrcSet?: { url: string; width: number; height: number }[];
} & Omit<ImageProps, 'children'> &
  HTMLDataTrackerProps;

export default function NextImage({
  src: originalSrc,
  width,
  height,
  alt,
  objectFit,
  classNames,
  quality = 70,
  ...props
}: NextImageProps) {
  const maxWidth = 1280;

  const src = debucketizeImageUrl(originalSrc as string);

  // Limit width to maxWidth and adjust height to maintain aspect ratio
  const adjustedWidth = width ? Math.min(Number(width), maxWidth) : 0;
  const adjustedHeight = height
    ? (adjustedWidth / Number(width)) * Number(height)
    : 0;

  return (
    <div className={cn('relative min-w-fit', classNames?.container)}>
      <Image
        className={cn(classNames?.image)}
        src={src}
        alt={alt || ''}
        fill={width || height ? false : true}
        quality={quality}
        style={{
          objectFit: objectFit || 'contain',
        }}
        data-tracker-component='next-image'
        {...props}
        unoptimized={src.endsWith('.svg')}
        width={adjustedWidth}
        height={adjustedHeight}
      />
    </div>
  );
}
